
import React, { useEffect, useState } from "react";
import "./ProcurementLifecycleReport.css";
import {
  fetchProcurementLifecycleReport,
  searchProcurementOrders,
} from "./procurementLifecycleReportService";

const STAGES = [
  { key: "bom", label: "Engineering BOM", shortLabel: "BOM" },
  { key: "indent", label: "Indent", shortLabel: "Indent" },
  {
    key: "purchase_request",
    label: "Purchase Request",
    shortLabel: "PR",
  },
  {
    key: "rfq",
    label: "Request for Quotation",
    shortLabel: "RFQ",
  },
  {
    key: "purchase_order",
    label: "Purchase Order",
    shortLabel: "PO",
  },
  {
    key: "grn",
    label: "Goods Receipt Note",
    shortLabel: "GRN",
  },
  {
    key: "purchase_invoice",
    label: "Purchase Invoice",
    shortLabel: "Invoice",
  },
];

const getOrderLines = (value) => {
  if (!value || typeof value !== "object") return [];

  if (Array.isArray(value)) {
    return value.flatMap((item) => getOrderLines(item));
  }

  const result = [];

  Object.entries(value).forEach(([key, item]) => {
    if (
      key.toLowerCase().replace(/_/g, " ").trim() === "order lines" &&
      Array.isArray(item)
    ) {
      result.push(...item);
    } else if (item && typeof item === "object") {
      result.push(...getOrderLines(item));
    }
  });

  return result;
};

const ProcurementLifecycleReport = () => {
  const [sourceType, setSourceType] = useState("ORDER");
  const [sourceId, setSourceId] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [orderOptions, setOrderOptions] = useState([]);
  const [orderSearch, setOrderSearch] = useState("");
  const [showOrderOptions, setShowOrderOptions] = useState(false);
  const [orderSearchLoading, setOrderSearchLoading] = useState(false);

  const handleOrderSearch = async (keyword) => {
    setOrderSearch(keyword);
    setSourceId("");
    setShowOrderOptions(true);
    setError("");

    if (!keyword.trim()) {
      setOrderOptions([]);
      setOrderSearchLoading(false);
      return;
    }

    setOrderSearchLoading(true);

    try {
      const orders = await searchProcurementOrders(keyword);
      setOrderOptions(orders);
    } catch (err) {
      console.error("Failed to search Orders:", err);
      setOrderOptions([]);
    } finally {
      setOrderSearchLoading(false);
    }
  };

  const handleSourceTypeChange = (event) => {
    setSourceType(event.target.value);
    setSourceId("");
    setOrderSearch("");
    setOrderOptions([]);
    setShowOrderOptions(false);
    setOrderSearchLoading(false);
    setReport(null);
    setError("");
  };

  const handleGenerate = async (event) => {
    event.preventDefault();

    const id = Number(sourceId);

    if (!Number.isInteger(id) || id <= 0) {
      setError(
        sourceType === "ORDER"
          ? "Please search for and select an Order."
          : "Please enter a valid positive Project ID."
      );
      return;
    }

    setLoading(true);
    setError("");
    setReport(null);
    setShowOrderOptions(false);

    try {
      const response = await fetchProcurementLifecycleReport({
        source_type: sourceType,
        source_id: id,
      });

      const data = response?.data ?? response;

      if (!data?.stages) {
        throw new Error(
          response?.message ||
            "The API returned an unexpected report format."
        );
      }

      setReport(data);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to generate the Procurement Lifecycle Report."
      );
    } finally {
      setLoading(false);
    }
  };

  

const renderValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  if (typeof value === "object") {
    return (
      <details className="plr-nested-value">
        <summary>
          {Array.isArray(value)
            ? `${value.length} items — View details`
            : "View details"}
        </summary>
        {renderRecords(value)}
      </details>
    );
  }

  return String(value);
};

const renderRecords = (value) => {
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <p className="plr-empty">No records found.</p>;
    }

    if (
      value.every(
        (item) =>
          item !== null &&
          typeof item === "object" &&
          !Array.isArray(item)
      )
    ) {
      const columns = [
        ...new Set(value.flatMap((item) => Object.keys(item))),
      ];

      return (
        <div className="plr-data-table-wrapper">
          <table className="plr-data-table">
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column}>
                    {column.replace(/_/g, " ")}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {value.map((record, index) => (
                <tr key={record.id ?? index}>
                  {columns.map((column) => (
                    <td key={column}>
                      {renderValue(record[column])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return (
      <ul className="plr-value-list">
        {value.map((item, index) => (
          <li key={index}>{renderValue(item)}</li>
        ))}
      </ul>
    );
  }

  if (value && typeof value === "object") {
    const entries = Object.entries(value);

    const scalarEntries = entries.filter(
      ([, item]) =>
        item === null ||
        item === undefined ||
        typeof item !== "object"
    );

    const nestedEntries = entries.filter(
      ([, item]) => item !== null && typeof item === "object"
    );

    return (
      <>
        {scalarEntries.length > 0 && (
          <div className="plr-record">
            {scalarEntries.map(([key, item]) => (
              <div className="plr-field" key={key}>
                <span className="plr-field-label">
                  {key.replace(/_/g, " ")}
                </span>
                <span className="plr-field-value">
                  {renderValue(item)}
                </span>
              </div>
            ))}
          </div>
        )}

                
        {nestedEntries
        .slice()
        .sort(([keyA], [keyB]) => {
            const priority = (key) => {
            const normalized = key.toLowerCase().replace(/_/g, " ");

            if (normalized.includes("order line")) return 0;
            if (normalized.includes("bom")) return 1;
            return 2;
            };

            return priority(keyA) - priority(keyB);
        })
        .map(([key, item]) => (
            <div className="plr-subsection" key={key}>
            <h4>{key.replace(/_/g, " ")}</h4>
            {renderRecords(item)}
            </div>
        ))}

      </>
    );
  }

  return <p>{renderValue(value)}</p>;
};

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!event.target.closest(".plr-order-search")) {
        setShowOrderOptions(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  return (
    <div className="procurement-lifecycle-report">
      <div className="plr-page-header">
        <div>
          <h2>Procurement Lifecycle Report</h2>
          <p>
            Trace procurement documents from an Order or Project through
            BOM, Indent, PR, RFQ, PO, GRN and Purchase Invoice.
          </p>
        </div>
      </div>

      <form className="plr-filter-panel" onSubmit={handleGenerate}>
        <div className="plr-filter-field">
          <label htmlFor="plr-source-type">Starting Point</label>
          <select
            id="plr-source-type"
            value={sourceType}
            onChange={handleSourceTypeChange}
          >
            <option value="ORDER">Order</option>
            <option value="PROJECT">Project</option>
          </select>
        </div>

        <div className="plr-filter-field">
          <label htmlFor="plr-source-id">
            {sourceType === "ORDER" ? "Select Order" : "Project ID"}
          </label>

          {sourceType === "ORDER" ? (
            <div className="plr-order-search">
              <input
                id="plr-source-id"
                type="text"
                value={orderSearch}
                onChange={(event) =>
                  handleOrderSearch(event.target.value)
                }
                onFocus={() => setShowOrderOptions(true)}
                placeholder="Search order number or customer..."
                autoComplete="off"
                required
              />

              {showOrderOptions && orderSearch.trim() && (
                <div
                  className="plr-order-options"
                  role="listbox"
                  aria-label="Matching orders"
                >
                  {orderSearchLoading ? (
                    <div className="plr-order-message">
                      Searching orders...
                    </div>
                  ) : orderOptions.length > 0 ? (
                    orderOptions.map((order) => (
                      <button
                        key={order.id}
                        type="button"
                        className="plr-order-option"
                        role="option"
                        aria-selected={String(order.id) === sourceId}
                        onMouseDown={(event) =>
                          event.preventDefault()
                        }
                        onClick={() => {
                          setSourceId(String(order.id));
                          setOrderSearch(
                            `${order.order_no || `Order #${order.id}`}${
                              order.customer_name
                                ? ` — ${order.customer_name}`
                                : ""
                            }`
                          );
                          setShowOrderOptions(false);
                          setError("");
                        }}
                      >
                        <span className="plr-order-option-title">
                          {order.order_no || `Order #${order.id}`}
                        </span>
                        <span className="plr-order-option-subtitle">
                          {order.customer_name ||
                            "Customer not available"}
                        </span>
                      </button>
                    ))
                  ) : (
                    <div className="plr-order-message">
                      No orders found.
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <input
              id="plr-source-id"
              type="number"
              min="1"
              step="1"
              value={sourceId}
              onChange={(event) => setSourceId(event.target.value)}
              placeholder="Enter Project ID"
              required
            />
          )}
        </div>

        <button
          className="plr-generate-button"
          type="submit"
          disabled={loading}
        >
          {loading ? "Generating..." : "Generate Report"}
        </button>
      </form>

      {error && (
        <div className="plr-error" role="alert">
          {error}
        </div>
      )}

      {report && (
        <>
                    
            <details className="plr-source-summary" open>
            <summary className="plr-collapsible-summary">
                <h3>Selected Source</h3>
                <span className="plr-collapse-icon" aria-hidden="true">
                ▾
                </span>
            </summary>

            <div className="plr-source-summary-content">
                {renderRecords(report.source)}
            </div>
            </details>
            
            
{sourceType === "ORDER" && (() => {
  const orderLines = getOrderLines(report.stages?.bom);

  if (orderLines.length === 0) return null;

  const columns = [
    ...new Set(orderLines.flatMap((line) => Object.keys(line))),
  ];

  return (
    <section className="plr-order-lines">
      <h3>Order Lines</h3>

      <div className="plr-data-table-wrapper">
        <table className="plr-data-table">
          <thead>
            <tr>
              {columns.map((key) => (
                <th key={key}>{key.replace(/_/g, " ")}</th>
              ))}
            </tr>
          </thead>

          <tbody>
            {orderLines.map((line, index) => (
              <tr key={line.id ?? index}>
                {columns.map((key) => (
                  <td key={key}>{renderValue(line[key])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
})()}


          <section className="plr-stage-counts">
            <div className="plr-section-heading">
              <h3>Lifecycle Summary</h3>
              <span>{STAGES.length} stages</span>
            </div>

            <div className="plr-count-grid">
              {STAGES.map((stage) => (
                <div className="plr-count-card" key={stage.key}>
                  <span>{stage.shortLabel}</span>
                  <strong>
                    {report.stage_counts?.[stage.key] ?? 0}
                  </strong>
                </div>
              ))}
            </div>
          </section>

          <section className="plr-lifecycle-stages">
            <h3>Lifecycle Details</h3>

            {STAGES.map((stage, index) => {
              const stageData = report.stages?.[stage.key];

              return (
                <details className="plr-stage" key={stage.key}>
                  <summary>
                    <span className="plr-stage-number">
                      {index + 1}
                    </span>
                    <span className="plr-stage-label">
                      {stage.label}
                    </span>
                    <span className="plr-stage-count">
                      {report.stage_counts?.[stage.key] ?? 0} records
                    </span>
                    <span className="plr-stage-chevron" aria-hidden="true">
                      ▾
                    </span>
                  </summary>

                  <div className="plr-stage-content">
                    {stageData ? (
                      renderRecords(stageData)
                    ) : (
                      <p className="plr-empty">
                        No data available for this stage.
                      </p>
                    )}
                  </div>
                </details>
              );
            })}
          </section>

          {Array.isArray(report.traceability_notes) &&
            report.traceability_notes.length > 0 && (
              <section className="plr-notes">
                <h3>Traceability Notes</h3>
                <ul>
                  {report.traceability_notes.map((note, index) => (
                    <li key={index}>{note}</li>
                  ))}
                </ul>
              </section>
            )}
        </>
      )}
    </div>
  );
};

export default ProcurementLifecycleReport;
